import { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import { colors, r4, s24 } from '@ardoq/design-tokens';
import { focusableCSS, getFocusRingCSS } from '@ardoq/style-helpers';
import { UnstyledButton } from '@ardoq/button';

const swatchBaseCSS = css`
  width: ${s24};
  height: ${s24};
  border-radius: ${r4};
  position: relative;
  display: block;
`;

const ReadOnlySwatch = styled.div`
  ${swatchBaseCSS};
`;

type InteractiveSwatchProps = {
  $isActive?: boolean;
  $hasError?: boolean;
};

const InteractiveSwatch = styled(UnstyledButton)<InteractiveSwatchProps>`
  ${({ $isActive }) =>
    $isActive &&
    css`
      && {
        ${getFocusRingCSS(1, colors.black950)};
      }
    `}

  ${swatchBaseCSS};
  ${focusableCSS};

  ${({ $hasError }) => $hasError && getFocusRingCSS(1, colors.borderError)};
`;

type ReadOnlyColorSwatchProps = {
  color: string;
  isReadOnly: true;
  children?: ReactNode;
};

type InteractiveColorSwatchProps = {
  color: string;
  isReadOnly?: false;
  isActive: boolean;
  hasError?: boolean;
  ariaLabel?: string;
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
} & Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'onClick'
  | 'onKeyDown'
  | 'tabIndex'
  | 'aria-pressed'
  | 'aria-haspopup'
  | 'aria-expanded'
>;

type ColorSwatchProps = ReadOnlyColorSwatchProps | InteractiveColorSwatchProps;

export const ColorSwatch = (props: ColorSwatchProps) => {
  if (props.isReadOnly) {
    return (
      <ReadOnlySwatch
        data-testid="color-swatch"
        style={{ backgroundColor: props.color }}
      >
        {props.children}
      </ReadOnlySwatch>
    );
  }

  const {
    color,
    isActive,
    hasError,
    ariaLabel,
    children,
    ref,
    ...buttonProps
  } = props;

  return (
    <InteractiveSwatch
      ref={ref}
      type="button"
      aria-label={ariaLabel ?? `Color ${color}`}
      data-testid="color-swatch"
      style={{ backgroundColor: color }}
      $isActive={isActive}
      $hasError={hasError}
      {...buttonProps}
    >
      {children}
    </InteractiveSwatch>
  );
};
